// src/utils/apiUtils.js

/**
 * Determines the base URL for the backend API based on the environment.
 * @returns {string} The base URL for API requests.
 */
const getApiBaseUrl = () => {
  // Use environment variable first
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // Fallback for development
  return 'http://localhost:4000/api';
};

const API_BASE_URL = getApiBaseUrl();

// --- Helper functions for tokens ---
/**
 * Retrieves the current authentication tokens from local storage.
 * @returns {{accessToken: string|null, refreshToken: string|null}}
 */
const getTokens = () => ({
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
});

/**
 * Updates the access token and its expiration in local storage.
 * @param {string} accessToken - The new JWT access token.
 * @param {string} accessTokenExpiry - The expiration timestamp of the new token.
 */
const updateTokens = (accessToken, accessTokenExpiry) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('accessTokenExpiry', accessTokenExpiry);
};

/**
 * Clears all authentication-related data from local storage.
 * Used during logout or when a session expires.
 */
const clearTokens = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessTokenExpiry');
  localStorage.removeItem('refreshTokenExpiry');
};

/**
 * Redirects the user to the home/landing page forcefully.
 */
const redirectToHome = () => {
  window.location.href = '/';
};

// ====================================================================
// FOR AUTHENTICATED ROUTES (When user is already logged in)
// ====================================================================
/**
 * Core fetch wrapper for authenticated endpoints. 
 * Automatically attaches the Bearer token and refresh token headers.
 * Intercepts 401 Unauthorized responses to log the user out if the session is fully expired.
 * Also captures and stores any new access tokens returned by the server via `x-new-access-token`.
 * 
 * @param {string} endpoint - The API endpoint relative to the base URL (e.g., `/users/me`).
 * @param {RequestInit} [options={}] - Standard fetch options (method, headers, body, etc.).
 * @returns {Promise<Response>} The fetch Response object.
 * @throws {Error} If authentication is missing or the session has expired.
 */
export const apiRequest = async (endpoint, options = {}) => {
  const { accessToken, refreshToken } = getTokens();

  if (!accessToken) {
    console.error('Authentication error: No access token found.');
    clearTokens();
    redirectToHome();
    throw new Error('Authentication required. Please log in again.');
  }

  const requestOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
      'x-refresh-token': refreshToken,
    },
  };

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, requestOptions);

    const newAccessToken = response.headers.get('x-new-access-token');
    if (newAccessToken) {
      updateTokens(newAccessToken, response.headers.get('x-access-token-expiry'));
    }
    
    if (response.status === 401) {
        console.error('Session expired. Logging out.');
        clearTokens();
        redirectToHome();
        throw new Error('Session expired.');
    }

    return response;
  } catch (error) {
    console.error(`API request to ${endpoint} failed:`, error);
    throw error;
  }
};


// ====================================================================
// FOR PUBLIC ROUTES (Login, Signup, Check Username, etc.)
// ====================================================================
/**
 * Core fetch wrapper for public endpoints that do not require authentication.
 * Useful for login, registration, and health checks.
 * 
 * @param {string} endpoint - The API endpoint relative to the base URL.
 * @param {RequestInit} [options={}] - Standard fetch options.
 * @returns {Promise<Response>} The fetch Response object.
 */
export const publicApiRequest = async (endpoint, options = {}) => {
    const requestOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };
  
    const url = `${getApiBaseUrl()}${endpoint}`;
  
    try {
      const response = await fetch(url, requestOptions);
      return response;
    } catch (error) {
      console.error(`Public API request to ${endpoint} failed:`, error);
      throw error;
    }
};

// --- Convenience methods for AUTHENTICATED calls ---
export const apiGet = (endpoint, options) => apiRequest(endpoint, { ...options, method: 'GET' });

/**
 * Enhanced GET request with local storage caching.
 * Reduces API hits for relatively static data (like dashboard summaries).
 * 
 * @param {string} endpoint - API endpoint.
 * @param {number} [ttl=300000] - Time to live in milliseconds (default 5 mins).
 * @returns {Promise<Response|Object>} Mocked response object or fresh fetch result.
 */
export const apiGetCached = async (endpoint, ttl = 300000) => {
    const cacheKey = `api_cache_${endpoint}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
        try {
            const { data, expiry } = JSON.parse(cached);
            if (Date.now() < expiry) {
                return {
                    ok: true,
                    cached: true,
                    json: async () => data
                };
            }
        } catch (e) {
            localStorage.removeItem(cacheKey);
        }
    }

    const response = await apiGet(endpoint);
    if (response.ok) {
        try {
            const data = await response.clone().json();
            localStorage.setItem(cacheKey, JSON.stringify({
                data,
                expiry: Date.now() + ttl
            }));
        } catch (e) {
            console.warn('Failed to cache API response:', e);
        }
    }
    return response;
};

export const apiPost = (endpoint, data, options) => apiRequest(endpoint, { ...options, method: 'POST', body: JSON.stringify(data) });
export const apiPut = (endpoint, data, options) => apiRequest(endpoint, { ...options, method: 'PUT', body: JSON.stringify(data) });
export const apiDelete = (endpoint, options) => apiRequest(endpoint, { ...options, method: 'DELETE' });

// --- Convenience methods for PUBLIC calls ---
export const publicApiGet = (endpoint, options) => publicApiRequest(endpoint, { ...options, method: 'GET' });

/**
 * Enhanced PUBLIC GET request with local storage caching.
 * 
 * @param {string} endpoint - API endpoint.
 * @param {number} [ttl=300000] - Time to live in milliseconds (default 5 mins).
 * @returns {Promise<Response|Object>} Mocked response object or fresh fetch result.
 */
export const publicApiGetCached = async (endpoint, ttl = 300000) => {
    const cacheKey = `api_cache_public_${endpoint}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
        try {
            const { data, expiry } = JSON.parse(cached);
            if (Date.now() < expiry) {
                return {
                    ok: true,
                    cached: true,
                    json: async () => data
                };
            }
        } catch (e) {
            localStorage.removeItem(cacheKey);
        }
    }

    const response = await publicApiGet(endpoint);
    if (response.ok) {
        try {
            const data = await response.clone().json();
            localStorage.setItem(cacheKey, JSON.stringify({
                data,
                expiry: Date.now() + ttl
            }));
        } catch (e) {
            console.warn('Failed to cache API response:', e);
        }
    }
    return response;
};

export const publicApiPost = (endpoint, data, options) => publicApiRequest(endpoint, { ...options, method: 'POST', body: JSON.stringify(data) });